import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Loader2,
  ArrowDown,
  ArrowUp,
  FileText,
  Mail,
  MessageSquareText,
  Plus,
  Sparkles,
  Square,
  User,
  WandSparkles,
  X,
  Check,
  ChevronDown,
  History,
  PanelRight,
} from "lucide-react";
import { useChatAssistant } from "core";
import type { ChatMessage, IngestionProgress, SocketStatus, ThinkingStep } from "core";
import { isImportComplete } from "core/features/chat-assistant/importProgress";
/* Straight from the module, not the `core` barrel: the barrel pulls in axios and
   Amplify, so tests mock it wholesale and these would come back undefined. */
import { ASSISTANT_NAME, ASSISTANT_ROLE } from "core/features/chat-assistant/config";
import { useLocation } from "react-router-dom";
import { cn } from "lib/utils";
import { MarkdownMessage } from "./MarkdownMessage";
import { TemplatePreviewPanel } from "./TemplatePreviewPanel";
import { ConversationList } from "./ConversationList";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

const SUGGESTIONS = [
  {
    icon: Mail,
    title: "Create a campaign",
    hint: "Plan a launch end to end",
    prompt: "Help me create a complete email campaign for a new product launch.",
  },
  {
    icon: WandSparkles,
    title: "Improve my content",
    hint: "Lift engagement on a draft",
    prompt: "Review my campaign content and suggest ways to improve engagement.",
  },
  {
    icon: FileText,
    title: "Summarize performance",
    hint: "See what's working",
    prompt: "Summarize my recent campaign performance and recommend next steps.",
  },
  {
    icon: MessageSquareText,
    title: "Ask anything",
    hint: "Explore what I can do",
    prompt: "What can you help me do across my marketing workspace?",
  },
];

const SLASH_COMMANDS = [
  { command: "/campaign", label: "Create campaign", description: "Plan and create a marketing campaign", icon: Mail },
  { command: "/template", label: "Create template", description: "Create a reusable email template", icon: FileText },
  { command: "/identity", label: "Manage identity", description: "Create or update brand identity", icon: User },
];

const PREVIEW_WIDTH_KEY = "merko.aiPlayground.previewWidth";
const PREVIEW_WIDTH_DEFAULT = 46;
const PREVIEW_WIDTH_MIN = 26;
const PREVIEW_WIDTH_MAX = 72;

const clampPreviewWidth = (value: number) =>
  Math.min(PREVIEW_WIDTH_MAX, Math.max(PREVIEW_WIDTH_MIN, value));

const GUTTER = "px-4 sm:px-6 lg:px-10 xl:px-16 2xl:px-24";
const isConnectionPending = (status: SocketStatus) => status === "connecting" || status === "closed";

const STATUS_ANNOUNCEMENT: Record<SocketStatus, string> = {
  connecting: "Connecting to the assistant",
  open: "Assistant connected",
  closed: "Connection lost. Reconnecting to the assistant",
  auth_failed: "Session expired. Sign in again to reconnect",
};

function ConnectionProgress({ status }: { status: SocketStatus }) {
  if (!isConnectionPending(status)) return null;
  return (
    <div
      className="absolute inset-x-0 top-0 z-30 h-0.5 overflow-hidden bg-accent-muted dark:bg-indigo-950"
      role="status"
      aria-live="polite"
    >
      <div className="h-full w-full origin-left animate-indeterminate bg-accent motion-reduce:animate-pulse" />
      <span className="sr-only">{STATUS_ANNOUNCEMENT[status]}</span>
    </div>
  );
}


function AssistantAvatar({ live = false }: { live?: boolean }) {
  return (
    <span className="relative flex h-8 w-8 shrink-0 items-center justify-center">
      {live && (
        <span
          className="absolute -inset-1 animate-pulse rounded-2xl bg-accent/25 blur-[6px] motion-reduce:animate-none"
          aria-hidden="true"
        />
      )}
      <span className="relative flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-accent to-indigo-400 text-white shadow-card">
        <Sparkles className="h-4 w-4" />
      </span>
    </span>
  );
}
/** Contact import: a CSV import, or ingestion the assistant runs during a reply. */
function IngestionProgressBar({ progress }: { progress: IngestionProgress }) {
  const done = isImportComplete(progress);
  const failed = progress.status === "failed";
  const stalled = !done && !failed && progress.status === "stalled";
  const percent = done ? 100 : Math.max(0, Math.min(100, Math.round(progress.percent)));
  const label = done
    ? "Contacts imported"
    : failed
      ? "Import failed"
      : stalled
        ? "Import stalled"
        : "Importing contacts…";
  const count = progress.total ? `${progress.processed ?? 0}/${progress.total} · ` : "";

  return (
    <div className="mt-3 max-w-sm">
      <div className="mb-1.5 flex items-center justify-between text-meta">
        <span
          className={cn(
            "font-medium",
            failed
              ? "text-red-600 dark:text-red-400"
              : stalled
                ? "text-amber-600 dark:text-amber-400"
                : "text-content",
          )}
        >
          {label}
        </span>
        <span className="tabular-nums text-content-muted">
          {count}
          {percent}%
        </span>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-dark-700"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-300",
            done ? "bg-emerald-500" : failed ? "bg-red-500" : stalled ? "bg-amber-500" : "bg-accent",
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
      {failed && (
        <p className="mt-1.5 text-meta text-content-muted">
          {/* CA-3: the server's own explanation when it sent one. The generic
              line is the fallback, not the default. */}
          {progress.reason
            ? progress.reason
            : "Some contacts may not have been added. Check the audience before trying again."}
        </p>
      )}
      {stalled && (
        <p className="mt-1.5 text-meta text-content-muted">
          No update for a while. The import may have failed — check the audience before uploading again.
        </p>
      )}
      {(failed || stalled) && <ImportRetryButton />}
    </div>
  );
}

/**
 * CA-3: "Retry by requesting a new presigned upload."
 *
 * The old presigned POST is single-use and expires, so this asks the assistant
 * for a fresh one instead of replaying it. Hidden when there is nothing to
 * retry, or while a reply is streaming.
 */
function ImportRetryButton() {
  /* Through the `core` hook rather than the store module: importing the store
     directly drags axios and Amplify into every test of this screen. */
  const { retryContactImport: retry, canRetryContactImport } = useChatAssistant();
  const canRetry = canRetryContactImport?.() ?? false;

  if (!canRetry || !retry) return null;

  return (
    <button
      type="button"
      onClick={() => retry()}
      className="mt-2 rounded-md border border-border px-2.5 py-1 text-meta font-medium text-content transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      Request a new upload link
    </button>
  );
}

const WORKING_AFTER_MS = 600;
/** A count-up only helps once the wait is long enough to feel wrong. */
const ELAPSED_AFTER_MS = 4000;
const LONG_WAIT_MS = 30000;

function formatElapsed(ms: number) {
  const total = Math.floor(ms / 1000);
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
}

function useQuietFor(lastChunkAtMs: number | null | undefined, isStreaming: boolean) {
  const [quietMs, setQuietMs] = useState(0);

  useEffect(() => {
    if (!isStreaming || !lastChunkAtMs) {
      setQuietMs(0);
      return;
    }
    const tick = () => setQuietMs(Date.now() - lastChunkAtMs);
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [lastChunkAtMs, isStreaming]);

  return quietMs;
}

/**
 * The assistant's progress lines.
 *
 * While a reply is working these are the only thing on screen, so they carry
 * its live state: the step in hand is marked as running and the ones behind it
 * as done. The moment the answer arrives they fold into one line — they are how
 * it got there, not the answer, and left open they stacked "Got it, working on
 * your request." above every finished reply in the conversation.
 */
function ThinkingTrail({
  steps,
  live,
  quietMs,
  thinkingMs,
  stayOpen = false,
}: {
  steps: ThinkingStep[];
  live: boolean;
  quietMs: number;
  thinkingMs?: number;
  /** A failed reply keeps its steps open: they are what there is to go on. */
  stayOpen?: boolean;
}) {
  const [open, setOpen] = useState(live || stayOpen);
  const wasLive = useRef(live);

  useEffect(() => {
    if (wasLive.current !== live) setOpen(live || stayOpen);
    wasLive.current = live;
  }, [live, stayOpen]);

  // Nothing to show for a settled reply that never reported a step.
  if (!steps.length && !live) return null;

  /* While it runs, a ghost row follows the steps: the rail carries on and a
     shimmer sits where the next line will land, so the panel reads as still
     producing rather than as a list that happens to have stopped. */
  const rail = (
    <ol className="flex flex-col">
      {steps.map((step, index) => {
        const running = live && index === steps.length - 1;
        const last = index === steps.length - 1 && !live;
        return (
          <li key={step.sequence} className="flex items-stretch gap-2.5">
            <span className="flex w-3.5 shrink-0 flex-col items-center">
              <span className="relative mt-[5px] flex h-3.5 w-3.5 items-center justify-center">
                {running ? (
                  <>
                    <span
                      aria-hidden="true"
                      className="absolute h-3.5 w-3.5 animate-ping rounded-full bg-accent/30 motion-reduce:hidden"
                    />
                    <span className="relative h-[7px] w-[7px] rounded-full bg-accent" />
                  </>
                ) : (
                  <Check className="h-3 w-3 text-slate-400 dark:text-slate-500" />
                )}
              </span>
              {!last && <span aria-hidden="true" className="mt-1 w-px flex-1 bg-line dark:bg-dark-700" />}
            </span>
            <span
              className={cn(
                "pb-2.5 text-[13px] leading-relaxed",
                running ? "font-medium text-content" : "text-content-muted",
              )}
            >
              {step.text}
            </span>
          </li>
        );
      })}

      {live && (
        <li className="flex items-stretch gap-2.5" aria-hidden="true">
          <span className="flex w-3.5 shrink-0 flex-col items-center">
            <span className="mt-[5px] flex h-3.5 w-3.5 items-center justify-center">
              <span className="h-[7px] w-[7px] rounded-full bg-line dark:bg-dark-600" />
            </span>
          </span>
          <span className="skeleton-sweep mt-[7px] block h-2.5 w-28 rounded-full" />
        </li>
      )}
    </ol>
  );

  /**
   * Running, the steps sit in a panel of their own.
   *
   * On their own they were a grey line beside a dot — the only thing on screen
   * for ten or more seconds, and it read as a stray caption rather than as the
   * assistant at work. The panel gives that wait somewhere to live: what is
   * happening, how long it has been, and that more is coming.
   */
  if (live) {
    const elapsed = quietMs >= ELAPSED_AFTER_MS ? formatElapsed(quietMs) : null;
    const slow = quietMs >= LONG_WAIT_MS;
    // Until the first step lands there is nothing to report but the wait itself.
    const label = steps.length ? "Working" : "Thinking";
    return (
      <div role="status" aria-label={`Assistant is ${label.toLowerCase()}`}>
        <div className="relative w-fit min-w-[15rem] max-w-[34rem] overflow-hidden rounded-2xl bg-surface-muted p-3.5 ring-1 ring-inset ring-black/5 dark:bg-dark-800 dark:ring-white/10">
          <span
            aria-hidden="true"
            className="aurora-drift pointer-events-none absolute -left-10 -top-16 h-36 w-36 rounded-full bg-accent/15 blur-3xl"
          />
          <span
            aria-hidden="true"
            className="aurora-drift pointer-events-none absolute -bottom-20 -right-6 h-36 w-36 rounded-full bg-indigo-400/15 blur-3xl [animation-delay:-3.5s]"
          />

          <div className="relative flex items-center gap-4">
            <span className="text-[13px] font-semibold text-content">{label}</span>
            {elapsed && (
              <span
                aria-hidden="true"
                className="ml-auto shrink-0 rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium tabular-nums text-content-muted dark:bg-dark-700"
              >
                {elapsed}
              </span>
            )}
          </div>

          <div className="relative mt-3">{rail}</div>

          {slow && (
            <p className="relative mt-1 text-[11.5px] text-content-muted">
              This is taking longer than usual — it is still going.
            </p>
          )}
        </div>
      </div>
    );
  }

  const summary = thinkingMs
    ? `Worked for ${formatElapsed(thinkingMs)}`
    : `${steps.length} step${steps.length === 1 ? "" : "s"}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        aria-expanded={open}
        className="-ml-1.5 inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[12.5px] text-content-muted transition hover:bg-slate-100 hover:text-content focus-ring dark:hover:bg-dark-800"
      >
        <span>{summary}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="mt-2">{rail}</div>}
    </div>
  );
}

/**
 * Progress lines that report nothing.
 *
 * Every reply opens with the same sentence, whatever was asked — it is the
 * backend's fixed greeting rather than a step. Shown, it was the panel's only
 * content for the first several seconds of every reply, so the card sat there
 * saying that work was happening and nothing about what.
 *
 * Matched exactly, and only this one line: anything the assistant actually
 * reports goes through untouched. The backend has been asked to drop it, and
 * then this goes too.
 */
const FILLER_STEPS = new Set(["got it, working on your request."]);

const reportedSteps = (steps: ThinkingStep[] | undefined): ThinkingStep[] =>
  (steps ?? []).filter((step) => !FILLER_STEPS.has(step.text.trim().toLowerCase()));

/** The whole reveal finishes inside this, however long the answer is. */
const REVEAL_TOTAL_MS = 700;
const REVEAL_STEP_MIN = 26;
const REVEAL_STEP_MAX = 90;

/**
 * Reveals a finished answer in reading order.
 *
 * `response` hands over the entire reply in one frame, so without this a long
 * answer lands in a single jump after ten or more seconds of silence.
 *
 * It staggers whole elements, never characters: a reveal by character would
 * show half-parsed markdown. A list counts as its items rather than as one
 * block — most of these replies are a line of prose and then ten numbered
 * points, and staggering the list as a unit left the entire list arriving at
 * once, which is the thing being fixed.
 *
 * Delays are set in a layout effect, before the browser paints, so the blocks
 * are already holding at their start frame. Set after paint they would appear,
 * vanish and fade back in.
 */
function RevealedAnswer({ content, className }: { content: string; className?: string }) {
  const host = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const root = host.current?.firstElementChild;
    if (!root) return;

    const blocks: HTMLElement[] = [];
    for (const child of Array.from(root.children) as HTMLElement[]) {
      const items =
        child.tagName === "UL" || child.tagName === "OL"
          ? (Array.from(child.children) as HTMLElement[])
          : [];
      if (items.length) blocks.push(...items);
      else blocks.push(child);
    }

    const step = Math.min(
      REVEAL_STEP_MAX,
      Math.max(REVEAL_STEP_MIN, REVEAL_TOTAL_MS / Math.max(blocks.length, 1)),
    );
    blocks.forEach((block, index) => {
      block.style.animationDelay = `${Math.round(index * step)}ms`;
      block.classList.add("answer-block");
    });
  }, [content]);

  return (
    <div ref={host} className={className}>
      <MarkdownMessage content={content} />
    </div>
  );
}

function WorkingIndicator({ quietMs }: { quietMs: number }) {
  const elapsed = quietMs >= ELAPSED_AFTER_MS ? formatElapsed(quietMs) : null;
  const slow = quietMs >= LONG_WAIT_MS;
  return (
    <div role="status" className="animate-slide-in-up pl-11 sm:pl-12">
      <div className="relative max-w-[42rem] overflow-hidden rounded-2xl bg-surface-muted p-4 ring-1 ring-inset ring-black/5 dark:bg-dark-800 dark:ring-white/10 sm:p-5">
        <span
          aria-hidden="true"
          className="aurora-drift pointer-events-none absolute -left-12 -top-20 h-44 w-44 rounded-full bg-accent/15 blur-3xl"
        />
        <span
          aria-hidden="true"
          className="aurora-drift pointer-events-none absolute -bottom-24 -right-8 h-44 w-44 rounded-full bg-indigo-400/15 blur-3xl [animation-delay:-3.5s]"
        />

        <div className="relative flex items-start gap-3">
          <span className="relative flex h-8 w-8 shrink-0 items-center justify-center">
            <span
              aria-hidden="true"
              className="absolute -inset-1 animate-pulse rounded-2xl bg-accent/25 blur-[6px] motion-reduce:animate-none"
            />
            <span className="relative flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-accent to-indigo-400 text-white shadow-card">
              <Sparkles className="h-4 w-4 animate-pulse motion-reduce:animate-none" />
            </span>
          </span>

          <span className="min-w-0">
            <span className="block text-title-sm font-semibold text-content">Working on it</span>
            {slow && (
              <span className="mt-0.5 block text-meta text-content-muted">
                This is taking longer than usual — it is still going.
              </span>
            )}
          </span>

          {elapsed && (
            <span
              aria-hidden="true"
              className="ml-auto shrink-0 self-start rounded-full bg-surface px-2 py-0.5 text-meta font-medium tabular-nums text-content-muted dark:bg-dark-700"
            >
              {elapsed}
            </span>
          )}
        </div>

        <div className="relative mt-4 space-y-2" aria-hidden="true">
          {[
            ["100%", "0ms"],
            ["82%", "-140ms"],
            ["56%", "-280ms"],
          ].map(([width, delay]) => (
            <span
              key={width}
              className="skeleton-sweep block h-2.5 rounded-full"
              style={{ width, animationDelay: delay }}
            />
          ))}
        </div>

        <span className="sr-only">The assistant is still working on your request.</span>
      </div>
    </div>
  );
}


type SaveState = {
  status: "saving" | "saved" | "needs-name" | "failed";
  name?: string | null;
  reason?: string;
  /** CA-6: 422 invalid_model_output — the document, not the save, was rejected. */
  invalidModelOutput?: boolean;
};

/*
 * Thumbnail geometry.
 *
 * 900 is wide enough that an email's own table — 600 to 700px, near enough
 * always — sits inside it with its page colour either side, the way the desktop
 * preview shows it. 680 clipped the wider ones on the right; 1040 stretched the
 * narrow ones into a layout no recipient will ever see.
 *
 * The frame is rendered far taller than the box and simply clipped, so its
 * height is not a third number to keep in step with the other two.
 *
 * The scale is measured, not worked out. Writing it as 520 ÷ 900 assumed the
 * dialog's inner width, and the assumption was wrong: `DialogContent` pads its
 * own wrapper, so the box was 478px and the thumbnail overhung it by 42.
 */
const THUMB_WIDTH = 900;
const THUMB_VISIBLE_HEIGHT = 160;

/**
 * A first suggestion for the name, read out of the template's own title or
 * leading heading.
 *
 * The dialog opened with an empty box and a disabled Save, so every save began
 * with the same small chore. This is the email's own words, not an invention —
 * and it arrives selected, so typing over it costs nothing.
 */
function suggestedName(html: string | null): string {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text =
      doc.querySelector("title")?.textContent ||
      doc.querySelector("h1, h2, h3")?.textContent ||
      "";
    return text.replace(/\s+/g, " ").trim().slice(0, 60);
  } catch {
    return "";
  }
}

function SaveTemplateDialog({
  state,
  html,
  onSubmit,
  onDismiss,
}: {
  state: SaveState;
  html: string | null;
  onSubmit: (name: string) => void;
  onDismiss: () => void;
}) {
  const [typed, setTyped] = useState(() => suggestedName(html));

  /* A callback ref, not an effect reading a ref. The dialog's content is
     portalled, so on the first layout pass the node was not attached yet, the
     effect took its `if (!el) return` and never came back — the observer was
     never set up and the frame sat at scale(0), invisible. This runs at the
     moment the node attaches, whenever that is. */
  const [thumbWidth, setThumbWidth] = useState(0);
  const thumbObserver = useRef<ResizeObserver | null>(null);
  const attachThumb = useCallback((el: HTMLDivElement | null) => {
    thumbObserver.current?.disconnect();
    thumbObserver.current = null;
    if (!el) return;
    setThumbWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      setThumbWidth((prev) => (prev === width ? prev : width));
    });
    observer.observe(el);
    thumbObserver.current = observer;
  }, []);
  const thumbScale = thumbWidth ? thumbWidth / THUMB_WIDTH : 0;

  const busy = state.status === "saving";
  const name = (state.name || typed).trim();
  const done = state.status === "saved";
  /* CA-6: a 422 `invalid_model_output` rejected the document itself, so saving
     the same bytes again cannot succeed — the template has to be generated
     afresh. Offering "Try again" here would just reproduce the failure. */
  const rejected = state.status === "failed" && !!state.invalidModelOutput;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // A save in flight is not something to walk away from half-way.
        if (!next && !busy) onDismiss();
      }}
    >
      <DialogContent size="md" showClose={!busy} className="overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {done ? "Template saved" : state.status === "failed" ? "The template wasn’t saved" : "Save template"}
          </DialogTitle>
          <DialogDescription>
            {done
              ? "It is in Templates now, ready to use in a campaign."
              : state.status === "failed"
                ? state.reason || "The save did not go through."
                : "Give it a name so you can find it later. It is not saved yet."}
          </DialogDescription>
        </DialogHeader>

        <div className="pt-4">
          {html && (
            <div
              ref={attachThumb}
              className="relative mb-4 overflow-hidden rounded-xl border border-line bg-white"
              style={{ height: THUMB_VISIBLE_HEIGHT }}
            >
              <iframe
                title="Template being saved"
                srcDoc={html}
                sandbox=""
                scrolling="no"
                aria-hidden="true"
                tabIndex={-1}
                className="pointer-events-none block origin-top-left border-none"
                style={{
                  width: THUMB_WIDTH,
                  height: THUMB_VISIBLE_HEIGHT * 3,
                  transform: `scale(${thumbScale})`,
                }}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/15 to-transparent"
              />
            </div>
          )}

          {rejected && (
            <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-meta text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              Ask the assistant to generate the template again — this one was
              rejected as invalid, so saving it again will not help.
            </p>
          )}

          {!done && !rejected && (
            <div>
              <label htmlFor="template-name" className="block text-ui font-semibold text-content">
                Template name
              </label>
              <input
                id="template-name"
                value={busy && state.name ? state.name : typed}
                onChange={(event) => setTyped(event.target.value)}
                onFocus={(event) => event.target.select()}
                disabled={busy}
                autoFocus
                placeholder="Diwali — festive offer"
                className="mt-1.5 h-10 w-full rounded-xl border border-line bg-surface px-3 text-ui text-content focus-ring disabled:opacity-60"
              />
              <p className="mt-1.5 text-meta text-content-muted">
                {busy ? `Saving “${name}”…` : "Saved templates appear under Templates."}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          {done || rejected ? (
            <button
              type="button"
              onClick={onDismiss}
              className="h-10 rounded-xl bg-brand px-4 text-ui font-semibold text-brand-fg hover:bg-brand-hover focus-ring"
            >
              {rejected ? "Close" : "Done"}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                className="h-10 rounded-xl px-3 text-ui font-semibold text-content-muted hover:text-content focus-ring disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onSubmit(state.status === "failed" ? name : typed)}
                disabled={busy || !(state.status === "failed" ? name : typed.trim())}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-brand px-4 text-ui font-semibold text-brand-fg hover:bg-brand-hover focus-ring disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                {busy ? "Saving…" : state.status === "failed" ? "Try again" : "Save template"}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AIPlayground() {
  const {
    socketStatus,
    messages,
    channels = [],
    activeChannelId = null,
    isLoadingChannels = false,
    channelsError = null,
    isHistoryLoading = false,
    isStreaming,
    isStopping,
    lastSocketError,
    isConfigured,
    sendPrompt,
    stopMessage,
    startNewChat,
    selectChannel,
    renameChannel,
    deleteChannel,
    listChannels,
    retryConnection,
    clearError,
    generatedTemplate,
    templateSave,
    submitTemplateName,
    dismissTemplateSave,
    lastChunkAtMs,
  } = useChatAssistant();

  const quietMs = useQuietFor(lastChunkAtMs, isStreaming);
  /* Matched by status, not position: an import bar can be appended below the
     reply while it is still streaming.

     The text has to be on screen already. A reply with none — nothing yet, or
     only thinking lines — shows its own live state inside the bubble, and this
     card underneath it read as a second, louder copy of the same thing. The
     card is for the other case: the answer looks finished and the socket has
     gone quiet, which is what a template generation looks like. */
  const isWorking =
    isStreaming &&
    quietMs >= WORKING_AFTER_MS &&
    messages.some((m) => m.sender === "assistant" && m.status === "streaming" && !!m.message);
  const location = useLocation();
  const seededPrompt = (location.state as { prompt?: string } | null)?.prompt ?? "";

  const [input, setInput] = useState(seededPrompt);
  const [selectedCommand, setSelectedCommand] = useState<(typeof SLASH_COMMANDS)[number] | null>(null);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const previewedVersionRef = useRef<number | null>(null);
  const [previewWidth, setPreviewWidth] = useState(() => {
    try {
      const stored = Number(window.localStorage.getItem(PREVIEW_WIDTH_KEY));
      return Number.isFinite(stored) && stored > 0 ? clampPreviewWidth(stored) : PREVIEW_WIDTH_DEFAULT;
    } catch {
      return PREVIEW_WIDTH_DEFAULT;
    }
  });
  const [isResizing, setIsResizing] = useState(false);
  const splitRef = useRef<HTMLDivElement | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isPinnedRef = useRef(true);

  const isConnected = socketStatus === "open";
  const composerDisabled = !isConnected || isStreaming || isHistoryLoading;
  const hasConversation = messages.length > 0;
  const isOpeningConversation = isHistoryLoading && !hasConversation;
  const activeChannel = channels.find((channel) => channel.channel_id === activeChannelId);

  const commandQuery = !selectedCommand && /^\/[^\s]*$/.test(input) ? input.slice(1).toLowerCase() : null;
  const visibleCommands = useMemo(
    () =>
      commandQuery === null
        ? []
        : SLASH_COMMANDS.filter(({ command, label }) =>
            `${command} ${label}`.toLowerCase().includes(commandQuery),
          ),
    [commandQuery],
  );
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isPinnedRef.current = distanceFromBottom < 120;
    setShowJumpToLatest(distanceFromBottom > 240);
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    isPinnedRef.current = true;
    setShowJumpToLatest(false);
  }, []);

  /* The history panel is an overlay, so Escape has to close it — the composer
     below keeps focus and would otherwise swallow the key. */
  useEffect(() => {
    if (!showHistory) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowHistory(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showHistory]);

  useEffect(() => {
    if (!generatedTemplate) return;
    if (previewedVersionRef.current === generatedTemplate.receivedAtMs) return;
    previewedVersionRef.current = generatedTemplate.receivedAtMs;
    setIsPreviewOpen(true);
  }, [generatedTemplate]);

  useEffect(() => {
    if (!isPinnedRef.current) return;
    const frame = requestAnimationFrame(() => scrollToBottom("auto"));
    return () => cancelAnimationFrame(frame);
  }, [messages, scrollToBottom]);

  const commitPreviewWidth = useCallback((value: number) => {
    const next = clampPreviewWidth(value);
    setPreviewWidth(next);
    try {
      window.localStorage.setItem(PREVIEW_WIDTH_KEY, String(Math.round(next)));
    } catch {
      /* Private browsing and the like. The split still works for this session. */
    }
  }, []);

  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const container = splitRef.current;
    if (!container) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizing(true);

    const bounds = container.getBoundingClientRect();
    const onMove = (move: PointerEvent) => {
      commitPreviewWidth(((bounds.right - move.clientX) / bounds.width) * 100);
    };
    const onUp = () => {
      setIsResizing(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 8 : 2;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      commitPreviewWidth(previewWidth + step);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      commitPreviewWidth(previewWidth - step);
    } else if (event.key === "Home") {
      event.preventDefault();
      commitPreviewWidth(PREVIEW_WIDTH_DEFAULT);
    }
  };

  const resetComposer = () => {
    setInput("");
    setSelectedCommand(null);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const submit = (value?: string) => {
    const content = (value ?? `${selectedCommand?.command ?? ""} ${input}`).trim();
    if (!content || isStreaming || !isConnected) return;
    resetComposer();
    isPinnedRef.current = true;
    sendPrompt(content);
  };

  const handleNewChat = () => {
    resetComposer();
    setIsPreviewOpen(false);
    setShowHistory(false);
    startNewChat();
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleSelectChannel = (channelId: string) => {
    setShowHistory(false);
    if (channelId === activeChannelId) return;
    setIsPreviewOpen(false);
    isPinnedRef.current = true;
    selectChannel(channelId);
  };

  const renderUserText = (content: string) => {
    const [first, ...rest] = content.split(/\s+/);
    const command = SLASH_COMMANDS.find((item) => item.command === first);
    if (!command) return content;
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-md bg-white/20 px-2 py-0.5 text-[11px] font-bold tracking-wide">
          {command.command}
        </span>
        {rest.join(" ")}
      </span>
    );
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.sender === "user";

    if (isUser) {
      return (
        <div key={message.clientId ?? message.message_id} className="flex animate-slide-in-up justify-end">
          <div className="max-w-[88%] rounded-2xl rounded-br-md bg-slate-900 px-4 py-3 text-title-sm leading-relaxed text-white shadow-card sm:max-w-[78%] lg:max-w-[62%] 2xl:max-w-[48%] dark:bg-dark-700">
            {renderUserText(message.message)}
          </div>
        </div>
      );
    }

    const isErrored = message.status === "error";
    const isLive = message.status === "pending" || message.status === "streaming";
    const thinking = reportedSteps(message.thinking);

    return (
      <div
        key={message.clientId ?? message.message_id}
        className="group/msg flex animate-slide-in-up gap-3 sm:gap-4"
        aria-busy={isLive}
      >
        <AssistantAvatar live={isLive} />
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "text-title-sm leading-relaxed text-slate-700 dark:text-slate-200",
              isErrored &&
                "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300",
            )}
          >
            {/* One slot for the whole live state. `pending` used to have a branch
                of its own, which put an identical "Thinking…" at a different place
                in the tree: the first frame moved it, React tore the old one down
                and built a new one, and the dots restarted mid-wait. */}
            {(thinking.length > 0 || (isLive && !message.message)) && (
              <ThinkingTrail
                steps={thinking}
                live={isLive && !message.message}
                quietMs={quietMs}
                thinkingMs={message.thinkingMs}
                stayOpen={isErrored}
              />
            )}
            {message.progress && <IngestionProgressBar progress={message.progress} />}
            {isErrored ? (
              <span className={cn(thinking.length > 0 && "mt-2 block")}>{message.message}</span>
            ) : (
              <>
                {message.message &&
                  (message.hasResponse ? (
                    /* Arrived in this session, so it is revealed. Reopening a
                       conversation must not re-animate every message in it. */
                    <RevealedAnswer content={message.message} className={cn(thinking.length > 0 && "mt-2.5")} />
                  ) : (
                    <div className={cn(thinking.length > 0 && "mt-2.5")}>
                      <MarkdownMessage content={message.message} />
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const showPreview = isPreviewOpen && Boolean(generatedTemplate);

  return (
    <div
      ref={splitRef}
      className={cn(
        "flex h-[calc(100dvh-3.5rem)] overflow-hidden bg-surface dark:bg-dark-900",
        /* While dragging, the pointer owns the whole split: without this the
           iframe swallows the move events and the drag stalls over it. */
        isResizing && "cursor-col-resize select-none [&_iframe]:pointer-events-none",
      )}
    >
      {showHistory && (
        <>
          <div
            className="animate-overlay-in fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px]"
            onClick={() => setShowHistory(false)}
            aria-hidden="true"
          />
          <aside
            id="ai-conversations"
            className="animate-drawer-in-right fixed inset-y-0 right-0 z-50 flex w-[23rem] max-w-[88vw] flex-col border-l border-line bg-surface shadow-elevated dark:border-dark-800 dark:bg-dark-900"
          >
            <ConversationList
              channels={channels}
              activeChannelId={activeChannelId}
              isLoading={isLoadingChannels}
              error={channelsError}
              disabled={!isConnected}
              onSelect={handleSelectChannel}
              onNewChat={handleNewChat}
              onRename={renameChannel}
              onDelete={deleteChannel}
              onRetry={listChannels}
              onClose={() => setShowHistory(false)}
            />
          </aside>
        </>
      )}

      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
      <ConnectionProgress status={socketStatus} />

      <header
        className={cn(
          "z-20 flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 bg-white/80 py-3 backdrop-blur-xl dark:border-dark-800 dark:bg-dark-900/80",
          GUTTER,
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-title-sm font-bold leading-tight text-slate-900 dark:text-white">
              {ASSISTANT_NAME}
            </h1>
            <p className="truncate text-xs text-slate-400">
              {activeChannel?.title || ASSISTANT_ROLE}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {socketStatus === "auth_failed" && (
            <span
              className="hidden items-center gap-2 rounded-full bg-red-50 px-3 py-1.5 sm:flex dark:bg-red-900/20"
              role="status"
              aria-live="polite"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              <span className="text-[11px] font-medium text-red-700 dark:text-red-300">Session expired</span>
            </span>
          )}
          {generatedTemplate && !isPreviewOpen && (
            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-[13px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 dark:border-dark-700 dark:text-slate-300 dark:hover:bg-dark-800"
            >
              <PanelRight className="h-4 w-4" />
              <span className="hidden sm:inline">Preview</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            aria-expanded={showHistory}
            aria-controls="ai-conversations"
            /* The label is hidden below `sm`; without this the button is nameless there. */
            aria-label="Chat history"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-[13px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 focus-ring dark:border-dark-700 dark:text-slate-300 dark:hover:bg-dark-800"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">History</span>
          </button>
          {hasConversation && !showHistory && (
            <button
              onClick={handleNewChat}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-[13px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 dark:border-dark-700 dark:text-slate-300 dark:hover:bg-dark-800"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New chat</span>
            </button>
          )}
        </div>
      </header>

      {(!isConfigured || lastSocketError) && (
        <div className={cn("shrink-0 pt-3", GUTTER)}>
          {!isConfigured ? (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Somethings went wrong
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">{lastSocketError!.message}</span>
              {lastSocketError!.kind === "auth" || lastSocketError!.kind === "network" ? (
                <button onClick={retryConnection} className="shrink-0 font-semibold underline">
                  {lastSocketError!.kind === "auth" ? "Reconnect" : "Try again"}
                </button>
              ) : (
                <button onClick={clearError} className="shrink-0 rounded p-0.5" aria-label="Dismiss">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <main
        ref={scrollRef}
        onScroll={handleScroll}
        className="no-scrollbar relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
      >
        {isOpeningConversation ? (
          <div className="flex min-h-full items-center justify-center gap-2 text-ui text-content-muted" role="status">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            Loading conversation…
          </div>
        ) : !hasConversation ? (
          <div className={cn("flex min-h-full flex-col items-center justify-center py-10", GUTTER)}>
            <div className="relative mb-6">
              <span className="absolute inset-0 scale-[2.4] rounded-full bg-accent/20 blur-3xl" aria-hidden="true" />
              <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent to-indigo-400 text-white shadow-elevated">
                <Sparkles className="h-7 w-7" />
              </span>
            </div>

            <h2 className="text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl dark:text-white">
              What would you like to do?
            </h2>
            <p className="mt-3 max-w-xl text-center text-sm leading-6 text-slate-500 sm:text-base dark:text-slate-400">
              Ask {ASSISTANT_NAME} to create, analyze, find, or update anything in your marketing workspace.
            </p>

            <div className="mt-8 grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {SUGGESTIONS.map(({ icon: Icon, title, hint, prompt }) => (
                <button
                  key={title}
                  onClick={() => submit(prompt)}
                  disabled={composerDisabled}
                  className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-surface p-4 text-left transition hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-card-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0 dark:border-dark-700 dark:bg-dark-800"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-muted text-accent transition group-hover:bg-accent group-hover:text-white dark:bg-dark-700">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-semibold text-slate-800 dark:text-slate-100">
                      {title}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-slate-400">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className={cn("flex flex-col gap-7 py-8", GUTTER)}>
            {messages.map(renderMessage)}

            {isWorking && <WorkingIndicator quietMs={quietMs} />}

            <span className="sr-only" aria-live="polite">
              {isWorking ? "Assistant is working" : isStreaming ? "Assistant is replying" : "Reply complete"}
            </span>
          </div>
        )}
      </main>

      {showJumpToLatest && hasConversation && (
        <button
          onClick={() => scrollToBottom()}
          className="absolute bottom-32 left-1/2 z-20 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-slate-200 bg-surface px-3.5 py-2 text-xs font-semibold text-slate-600 shadow-elevated transition hover:bg-slate-50 dark:border-dark-700 dark:bg-dark-800 dark:text-slate-200"
        >
          <ArrowDown className="h-3.5 w-3.5" /> Jump to latest
        </button>
      )}

      {/* ── Composer ───────────────────────────────────────────────────── */}
      <footer
        className={cn(
          "relative z-20 shrink-0 border-t border-slate-100 bg-white/80 pt-3 backdrop-blur-xl dark:border-dark-800 dark:bg-dark-900/80",
          "pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]",
          GUTTER,
        )}
      >
        {visibleCommands.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden">
            <div className={cn(GUTTER)}>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-surface p-1.5 shadow-elevated dark:border-dark-700 dark:bg-dark-800">
                <p className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Commands
                </p>
                {visibleCommands.map(({ command, label, description, icon: Icon }) => (
                  <button
                    key={command}
                    type="button"
                    onClick={() => {
                      setSelectedCommand(SLASH_COMMANDS.find((i) => i.command === command) ?? null);
                      setInput("");
                      requestAnimationFrame(() => textareaRef.current?.focus());
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-slate-50 dark:hover:bg-dark-700"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-muted text-accent dark:bg-dark-700">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">{label}</span>
                        <span className="text-[11px] font-medium text-accent">{command}</span>
                      </span>
                      <span className="block truncate text-micro text-slate-400">{description}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex items-end gap-2 rounded-2xl border bg-surface p-2 shadow-card transition dark:bg-dark-800",
            "border-slate-200 focus-within:border-accent/50 focus-within:shadow-card-hover focus-within:ring-4 focus-within:ring-accent/10",
            "dark:border-dark-700 dark:focus-within:border-accent",
          )}
        >
          {selectedCommand && (
            <span className="mb-0.5 ml-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-accent-muted px-2.5 text-[11px] font-bold text-accent dark:bg-dark-700">
              {selectedCommand.command}
              <button
                type="button"
                onClick={() => setSelectedCommand(null)}
                className="rounded p-0.5 transition hover:bg-accent/10"
                aria-label={`Remove ${selectedCommand.label} command`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(event) => {
              setInput(event.target.value);
              event.target.style.height = "auto";
              event.target.style.height = `${Math.min(event.target.scrollHeight, 200)}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !input && selectedCommand) {
                event.preventDefault();
                setSelectedCommand(null);
                return;
              }
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            rows={1}
            disabled={composerDisabled}
            placeholder={
              !isConnected
                ? `Connecting to ${ASSISTANT_NAME}…`
                : isWorking
                  ? `${ASSISTANT_NAME} is working…`
                  : isStreaming
                    ? `${ASSISTANT_NAME} is replying…`
                    : selectedCommand
                      ? "Add details…"
                      : `Ask ${ASSISTANT_NAME} to do anything…`
            }
            className="max-h-52 min-h-[2.25rem] flex-1 resize-none border-0 bg-transparent px-2 py-2 text-title-sm leading-6 text-slate-800 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed dark:text-slate-100"
          />

          {isStreaming ? (
            <button
              onClick={stopMessage}
              disabled={isStopping}
              className="flex h-9 shrink-0 items-center gap-2 rounded-xl bg-slate-900 px-3 text-[13px] font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60 dark:bg-dark-600 dark:hover:bg-dark-500"
              aria-label={isStopping ? "Stopping" : "Stop generating"}
            >
              <Square className="h-3 w-3 fill-current" />
              <span className="hidden sm:inline">{isStopping ? "Stopping…" : "Stop"}</span>
            </button>
          ) : (
            <button
              onClick={() => submit()}
              disabled={(!input.trim() && !selectedCommand) || composerDisabled}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-white transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-300 dark:disabled:bg-dark-700 dark:disabled:text-dark-500"
              aria-label="Send message"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          )}
        </div>
      </footer>
      </div>

      {/* Below `lg` there is no room for two columns, so the panel takes the
          screen; the close button is how the user gets back to the thread. */}
      {showPreview && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize preview"
          aria-valuenow={Math.round(previewWidth)}
          aria-valuemin={PREVIEW_WIDTH_MIN}
          aria-valuemax={PREVIEW_WIDTH_MAX}
          tabIndex={0}
          onPointerDown={handleResizePointerDown}
          onKeyDown={handleResizeKeyDown}
          onDoubleClick={() => commitPreviewWidth(PREVIEW_WIDTH_DEFAULT)}
          className={cn(
            "group relative hidden w-1.5 shrink-0 cursor-col-resize touch-none bg-line transition-colors focus-ring lg:block dark:bg-dark-800",
            isResizing ? "bg-accent" : "hover:bg-accent/60",
          )}
        >
          {/* A 1.5-unit bar is a small target, so the grab area is widened
              beyond what is painted. */}
          <span className="absolute inset-y-0 -left-1.5 -right-1.5" aria-hidden="true" />
        </div>
      )}

      {showPreview && (
        <div
          style={{ ["--preview-width" as string]: `${previewWidth}%` }}
          className="fixed inset-0 z-40 lg:static lg:z-auto lg:w-[var(--preview-width)] lg:min-w-[320px] lg:shrink-0"
        >
          <TemplatePreviewPanel
            html={generatedTemplate!.html}
            receivedAtMs={generatedTemplate!.receivedAtMs}
            isStreaming={isStreaming}
            onClose={() => setIsPreviewOpen(false)}
          />
        </div>
      )}

      {/* Naming is a decision to finish, so it takes focus rather than sitting
          in a transcript that keeps scrolling underneath it. */}
      {templateSave && (
        <SaveTemplateDialog
          state={templateSave}
          html={generatedTemplate?.html ?? null}
          onSubmit={(name) => void submitTemplateName(name)}
          onDismiss={dismissTemplateSave}
        />
      )}
    </div>
  );
}
